/** Home: the variant-A phone screen (chapter card, zigzag path, ПРОДОЛЖИТЬ) and «Сегодня» on desktop. */
import { useCallback, useState } from 'react';
import { routes, useRouter } from '../state/router.tsx';
import { useLoadedData } from '../state/app-data.tsx';
import { useCoursePath, usePrefetchLessons, useReviewQueue, useStats, type CoursePath } from '../state/selectors.ts';
import { stepKindLabel, useLessonPosition, type LessonPositionInfo } from '../state/use-lesson.ts';
import { DESKTOP, useMediaQuery } from '../state/use-media.ts';
import { greetingFor } from '../core/date.ts';
import { RoboCat } from '../ui/RoboCat.tsx';
import { Garland } from '../ui/Garland.tsx';
import { Flame, Icon } from '../ui/Icon.tsx';
import { LessonPath, lessonChar } from '../ui/LessonPath.tsx';
import { ChapterPath } from '../ui/ChapterPath.tsx';
import { Medallion } from '../ui/Medallion.tsx';
import { LeagueList } from '../ui/LeagueList.tsx';
import { PlacementOffer } from './PlacementScreen.tsx';
import { languageInfo } from '../ui/course.ts';
import { windowAround } from '../core/path.ts';
import type { LessonMeta } from '../data/api.ts';
import './home.css';

const numberFormat = new Intl.NumberFormat('ru-RU');

/** Short course mark for the chip: 中, EN, TR. */
const COURSE_MARK: Record<string, string> = { zh: '中', en: 'EN', tr: 'TR' };

function stepCaption(meta: LessonMeta, position: LessonPositionInfo | null): string {
  if (!position) return `Урок ${meta.number}`;
  return `Урок ${meta.number} · глава ${position.chapter.index} из 4 · ${position.chapter.name}`;
}

/** The green card: which chapter is next and how far into it the learner is. */
function ChapterCard({ path }: { path: CoursePath }) {
  const current = path.current;
  const position = useLessonPosition(current);

  if (!current) {
    const allDone = path.items.length > 0 && path.items.every((i) => i.status === 'done');
    return (
      <section className="chapter-card chapter-card--idle">
        <h2 className="chapter-card__title">{allDone ? 'Все уроки книги пройдены' : 'Уроки скоро появятся'}</h2>
        <p className="chapter-card__meta">Пока можно повторить слова или поговорить с репетитором.</p>
      </section>
    );
  }

  const share = position ? position.stepIndex / Math.max(1, position.chapter.steps.length) : 0;
  return (
    <section className="chapter-card" aria-labelledby="chapter-card-title">
      <p className="chapter-card__caps">
        {position ? `Глава ${position.chapter.index} · ` : ''}Урок {current.number}
      </p>
      <h2 id="chapter-card-title" className="chapter-card__title">
        <span className="han">{current.title}</span>
        {position && ` · ${position.chapter.name}`}
      </h2>
      <div className="chapter-card__bar" aria-hidden="true">
        <div className="chapter-card__fill" style={{ width: `${Math.round(share * 100)}%` }} />
      </div>
      {position && <p className="chapter-card__meta">≈ {Math.max(1, position.minutes)} мин</p>}
    </section>
  );
}

function PhoneHome({ onOpenCourses }: { onOpenCourses: () => void }) {
  const { navigate } = useRouter();
  const { courses, activeCourseId, progress } = useLoadedData();
  const stats = useStats();
  const path = useCoursePath();
  usePrefetchLessons(path);
  const openLesson = useCallback((id: string) => navigate(routes.lesson(id)), [navigate]);
  const [now] = useState(() => new Date());
  const reviews = useReviewQueue(now).cards.length;
  const course = courses.find((c) => c.id === activeCourseId);
  const language = languageInfo(course?.lang);
  const current = path.current;

  return (
    <div className="home">
      <header className="topbar">
        <button type="button" className="topbar__course" onClick={onOpenCourses} aria-label={`Курс: ${language.titleRu}. Сменить`}>
          <span className="topbar__mark han">{COURSE_MARK[course?.lang ?? ''] ?? '•'}</span>
          {course?.title ?? language.titleRu}
          <Icon name="chevronDown" size={18} />
        </button>
        <span className="topbar__stat topbar__stat--streak" aria-label={`Серия: ${stats.streak} дней подряд`}>
          <Flame size={22} /> {stats.streak}
        </span>
        <span className="topbar__stat topbar__stat--xp" aria-label={`${stats.weekly} XP за неделю`}>
          <Icon name="sparkle" size={22} /> {numberFormat.format(stats.weekly)}
        </span>
      </header>

      <div className="home__body">
        <PlacementOffer />
        <ChapterCard path={path} />

        <button type="button" className="quick-start" onClick={() => navigate(routes.quick)}>
          <span className="quick-start__icon" aria-hidden="true">
            <Icon name="bolt" size={26} />
          </span>
          <span className="quick-start__text">
            <strong>5 минут</strong>
            <span>{reviews > 0 ? `Быстрое повторение · ${Math.min(reviews, 15)} слов` : 'Быстрое повторение слов'}</span>
          </span>
          <Icon name="chevronRight" size={22} />
        </button>

        <div className="home__tiles">
          <div className="home-tile">
            <span className="home-tile__label">Цель дня</span>
            <span className="home-tile__value">
              {stats.goal.earned}/{stats.goal.goal} XP
            </span>
            <div className="progress progress--thin" aria-hidden="true">
              <div className="progress__fill" style={{ width: `${Math.min(100, (stats.goal.earned / stats.goal.goal) * 100)}%` }} />
            </div>
          </div>
          <button type="button" className="home-tile home-tile--link" onClick={() => navigate(routes.words)}>
            <span className="home-tile__label">Повторение</span>
            <span className="home-tile__value">{reviews > 0 ? `${reviews} слов` : 'всё готово'}</span>
            <span className="home-tile__go">
              Открыть <Icon name="chevronRight" size={16} />
            </span>
          </button>
        </div>

        {path.items.length > 0 && (
          <section aria-labelledby="path-title">
            <h2 id="path-title" className="visually-hidden">
              Путь · {path.bookTitle}
            </h2>
            <ChapterPath items={path.items} progress={progress} onOpen={openLesson} />
          </section>
        )}
      </div>

      {current && (
        <div className="home__cta">
          <button type="button" className="btn btn--primary btn--block" onClick={() => navigate(routes.lesson(current.id))}>
            Продолжить
          </button>
        </div>
      )}
    </div>
  );
}

function DesktopToday() {
  const { navigate } = useRouter();
  const { profile } = useLoadedData();
  const stats = useStats();
  const path = useCoursePath();
  usePrefetchLessons(path);
  const [now] = useState(() => new Date());
  const reviews = useReviewQueue(now).cards.length;
  const current = path.current;
  const position = useLessonPosition(current);
  const currentIndex = path.items.findIndex((i) => i.status === 'current');
  const wave = windowAround(path.items, currentIndex, 6);

  return (
    <div className="today">
      <header className="band band--desktop">
        <div>
          <h1 className="t-screen">
            {greetingFor(now)}, {profile.display_name}
          </h1>
          <p className="t-meta">
            {current ? `Сегодня — урок ${current.number}` : 'Сегодня — повторение'}
            {reviews > 0 ? ` и ${reviews} карточек на повторение.` : '.'}
          </p>
        </div>
        <div className="band__stats">
          <div className="stat">
            <span className="t-number">
              <Flame size={18} /> {stats.streak}
            </span>
            <span className="t-meta">дней подряд</span>
          </div>
          <div className="stat">
            <span className="row">
              <Garland filled={stats.goal.flags} label={`Цель дня: ${stats.goal.earned} из ${stats.goal.goal} XP`} />
              <span className="t-number">
                {stats.goal.earned}/{stats.goal.goal}
              </span>
            </span>
            <span className="t-meta">цель дня, XP</span>
          </div>
          <div className="stat">
            <span className="t-number">{numberFormat.format(stats.weekly)}</span>
            <span className="t-meta">XP за неделю</span>
          </div>
        </div>
        <div className="ornament band__ornament" aria-hidden="true" />
      </header>

      <div className="today__grid">
        <div className="stack stack--24">
          <PlacementOffer />
          <section className="card card--raised plan" aria-labelledby="plan-title">
            <div className="section-head">
              <h2 id="plan-title" className="t-card">
                План на сегодня
              </h2>
              {position && <span className="t-meta">≈ {position.minutes + Math.ceil(reviews / 4)} мин</span>}
            </div>
            {current ? (
              <div className="plan__row plan__row--lesson">
                <Medallion char={lessonChar(current)} state="current" size={56} />
                <div className="plan__text">
                  <strong>
                    {stepCaption(current, position)}
                    {position ? ` — ${stepKindLabel(position.stepKind)}` : ''}
                  </strong>
                  <span className="t-meta han">{current.title}</span>
                </div>
                <button type="button" className="btn btn--primary" onClick={() => navigate(routes.lesson(current.id))}>
                  Продолжить
                </button>
              </div>
            ) : (
              <p className="t-meta">Новых уроков пока нет.</p>
            )}
            <button type="button" className="plan__row plan__row--link" onClick={() => navigate(routes.words)}>
              <span className="plan__icon">
                <Icon name="refresh" />
              </span>
              <span className="plan__text">
                <strong>Повторение слов</strong>
                <span className="t-meta">{reviews > 0 ? `${reviews} ждут` : 'на сегодня всё'}</span>
              </span>
              <span className="plan__go">
                Начать <Icon name="chevronRight" size={18} />
              </span>
            </button>
            <button type="button" className="plan__row plan__row--link" onClick={() => navigate(routes.quick)}>
              <span className="plan__icon">
                <Icon name="bolt" />
              </span>
              <span className="plan__text">
                <strong>5 минут</strong>
                <span className="t-meta">Быстрое повторение слов</span>
              </span>
              <span className="plan__go">
                Начать <Icon name="chevronRight" size={18} />
              </span>
            </button>
            <div className="plan__tutor">
              <RoboCat size={46} alive />
              <div className="plan__text">
                <span className="tutor-label">ИИ-учитель</span>
                <span>Непонятное слово или правило? Спросите — объясню на примерах из урока.</span>
              </div>
              <button type="button" className="btn btn--secondary" onClick={() => navigate(routes.chat)}>
                Спросить
              </button>
            </div>
          </section>

          {wave.length > 0 && (
            <section className="card" aria-labelledby="wave-title">
              <div className="section-head">
                <h2 id="wave-title" className="t-card">
                  Путь · {path.bookTitle}
                </h2>
                <span className="t-meta">
                  {path.doneCount} из {path.items.length} уроков
                </span>
              </div>
              <LessonPath items={wave} layout="wave" onOpen={(id) => navigate(routes.lesson(id))} />
            </section>
          )}
        </div>

        <aside className="stack stack--24">
          <section className="card" aria-labelledby="league-title">
            <div className="section-head">
              <h2 id="league-title" className="t-section">
                Лига недели
              </h2>
            </div>
            <LeagueList limit={5} />
          </section>
        </aside>
      </div>
    </div>
  );
}

export function HomeScreen({ onOpenCourses }: { onOpenCourses: () => void }) {
  const desktop = useMediaQuery(DESKTOP);
  return desktop ? <DesktopToday /> : <PhoneHome onOpenCourses={onOpenCourses} />;
}
